import { Tooltip } from '@mui/material';
import UPEditActionIcon from 'components/edit-action-icon';
import { ClickableBox } from 'modules/contracts/styles';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CONTRACTS__ACTIONS_DISABLED } from 'store/constants';
import { ReactComponent as CloseAction } from '../../../../../../assets/icons/CloseContractAction.svg';
import { ReactComponent as ConfirmAction } from '../../../../../../assets/icons/ConfirmContract.svg';
import { ReactComponent as CopyAction } from '../../../../../../assets/icons/CopyContract.svg';
import { ReactComponent as DeleteAction } from '../../../../../../assets/icons/Delete.svg';
import { ReactComponent as DisabledInfoIcon } from '../../../../../../assets/icons/DisabledInfoIcon.svg';
import { ReactComponent as FillContractSlotAction } from '../../../../../../assets/icons/FillContractSlotIcon.svg';
import { ReactComponent as ReopenAction } from '../../../../../../assets/icons/ReopenServiceIcon.svg';
import { ReactComponent as ViewContractAction } from '../../../../../../assets/icons/ViewContractAction.svg';
import { ContractCardActionsContainer } from './styles';
import { ContractCardActionsProps } from './types';

const ContractCardActions: React.FC<ContractCardActionsProps> = ({
  cardId,
  isCopyEnabled,
  isConfirmed,
  isContractSlot,
  isDashboard,
  isDisabled,
  isEditable,
  isEmployee,
  cannotReopenContract,
  onClickConfirm,
  onClickCopy,
  onClickDelete,
  onClickEdit,
  onClickView,
  onClickFill,
  onClickReopen,
}) => {
  const [t] = useTranslation();

  const renderConfirmAction = () => (
    <ClickableBox sx={{ display: cannotReopenContract ? 'none' : 'block' }}>
      {!isContractSlot &&
        (isConfirmed ? (
          <ReopenAction onClick={onClickReopen} />
        ) : (
          <ConfirmAction
            data-gtm-event="contracts-calendar-view-confirm-contract"
            onClick={() => onClickConfirm(cardId)}
            id="contract-card-confirm-icon"
          />
        ))}
    </ClickableBox>
  );

  const renderDeleteAction = () => (
    <ClickableBox>
      <DeleteAction onClick={onClickDelete} />
    </ClickableBox>
  );

  const renderDisabledTooltip = () => (
    <Tooltip title={t(CONTRACTS__ACTIONS_DISABLED)}>
      <DisabledInfoIcon />
    </Tooltip>
  );

  const renderCopyAction = () => (
    <ClickableBox onClick={onClickCopy} data-gtm-event="contracts-copy-mode">
      {isCopyEnabled ? <CloseAction /> : <CopyAction />}
    </ClickableBox>
  );

  const renderEditAction = () => (
    <ClickableBox>
      {!isContractSlot ? (
        <UPEditActionIcon
          onClick={onClickEdit}
          data-gtm-event="contracts-calendar-view-edit-contract"
          id="contract-card-edit-icon"
        />
      ) : (
        <FillContractSlotAction onClick={onClickFill} />
      )}
    </ClickableBox>
  );

  const renderViewAction = () => (
    <ClickableBox onClick={onClickView}>
      <ViewContractAction />
    </ClickableBox>
  );

  const renderCardActions = () => {
    if (isEmployee) {
      return renderViewAction();
    }
    if (isEditable) {
      if (isDisabled) {
        return renderDisabledTooltip();
      }
      if (!isCopyEnabled && !isEmployee) {
        return (
          <>
            {renderDeleteAction()}
            {renderEditAction()}
          </>
        );
      }
    }
    return !isCopyEnabled && !isEmployee ? (
      <>
        {renderViewAction()}
        {renderConfirmAction()}
      </>
    ) : null;
  };

  return (
    <ContractCardActionsContainer>
      {renderCardActions()}
      {!isEmployee && !isDashboard && renderCopyAction()}
    </ContractCardActionsContainer>
  );
};

export default ContractCardActions;
